import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Card,
  CardContent,
  Avatar,
  Divider,
  Grid,
  CircularProgress,
  Alert,
  Fade,
  Zoom,
  Fab,
  Autocomplete
} from '@mui/material';
import { 
  Send as SendIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  DateRange as DateIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  KeyboardArrowDown as ScrollDownIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useIntl } from 'react-intl';
import { useAuthToken } from "../TokenContext";
import { useSelector } from "react-redux";

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  height: '100vh',
  overflow: 'hidden',
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #0f1419 0%, #1a1f2e 50%, #2d1b69 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #e1e8f0 50%, #d6e4f8 100%)',
  transition: 'all 0.3s ease',
  position: 'relative',
  flexGrow: 1,
}));

const MainContent = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: 'center',
  alignItems: 'flex-start',
  flex: 1,
  flexDirection: 'column',
  padding: '24px',
  overflow: 'hidden',
  background: theme.palette.mode === 'dark' 
    ? 'rgba(15, 20, 25, 0.85)'
    : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  margin: '20px',
  border: theme.palette.mode === 'dark' 
    ? '1px solid rgba(99, 102, 241, 0.2)'
    : '1px solid rgba(148, 163, 184, 0.3)',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 25px 50px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
    : '0 25px 50px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
  
  width: '100%',
  maxWidth: 'none',
  height: 'calc(100vh - 40px)',
  transition: 'all 0.3s ease',
  
  [theme.breakpoints.down("md")]: {
    margin: '10px',
    padding: '18px',
  },
  
  [theme.breakpoints.down("sm")]: {
    margin: '5px',
    padding: '16px',
    borderRadius: '20px',
  },
}));

const InputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  padding: '20px',
  background: theme.palette.mode === 'dark' 
    ? 'rgba(30, 41, 59, 0.8)'
    : 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(20px)',
  borderRadius: '20px',
  border: theme.palette.mode === 'dark' 
    ? '1px solid rgba(99, 102, 241, 0.3)'
    : '1px solid rgba(148, 163, 184, 0.2)',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 10px 30px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
    : '0 10px 30px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
  
  [theme.breakpoints.down("md")]: {
    padding: '16px',
  },
  
  [theme.breakpoints.down("sm")]: {
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    borderRadius: '16px',
  },
}));

const ModernCard = styled(Card)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'rgba(30, 41, 59, 0.6)'
    : 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(16px)',
  borderRadius: '16px',
  border: theme.palette.mode === 'dark' 
    ? '1px solid rgba(99, 102, 241, 0.2)'
    : '1px solid rgba(148, 163, 184, 0.15)',
  marginBottom: '16px',
  transition: 'all 0.3s ease',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 8px 32px rgba(0, 0, 0, 0.4)'
    : '0 8px 32px rgba(0, 0, 0, 0.06)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 20px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(99, 102, 241, 0.3)'
      : '0 20px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(59, 130, 246, 0.2)',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    height: '56px',
    borderRadius: '16px',
    background: theme.palette.mode === 'dark' 
      ? 'rgba(15, 20, 25, 0.8)'
      : 'rgba(255, 255, 255, 0.95)',
    border: 'none',
    fontSize: '16px',
    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b',
    '& fieldset': {
      border: theme.palette.mode === 'dark' 
        ? '1px solid rgba(99, 102, 241, 0.3)'
        : '1px solid rgba(148, 163, 184, 0.3)',
    },
    '&:hover fieldset': {
      border: theme.palette.mode === 'dark' 
        ? '1px solid rgba(99, 102, 241, 0.5)'
        : '1px solid rgba(59, 130, 246, 0.4)',
    },
    '&.Mui-focused fieldset': {
      border: theme.palette.mode === 'dark' 
        ? '2px solid #6366f1'
        : '2px solid #3b82f6',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '16px',
    fontWeight: '400',
    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b',
    '&::placeholder': {
      color: theme.palette.mode === 'dark' ? '#64748b' : '#64748b',
      opacity: 1,
    }
  }
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px',
  flexDirection: 'column',
  gap: '16px',
  background: theme.palette.mode === 'dark' 
    ? 'rgba(99, 102, 241, 0.1)'
    : 'rgba(59, 130, 246, 0.05)',
  borderRadius: '16px',
  border: theme.palette.mode === 'dark' 
    ? '2px solid rgba(99, 102, 241, 0.3)'
    : '2px solid rgba(59, 130, 246, 0.2)',
  minHeight: '120px',
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': {
      boxShadow: theme.palette.mode === 'dark' 
        ? '0 0 0 0 rgba(99, 102, 241, 0.4)'
        : '0 0 0 0 rgba(59, 130, 246, 0.4)',
    },
    '70%': {
      boxShadow: theme.palette.mode === 'dark' 
        ? '0 0 0 10px rgba(99, 102, 241, 0)'
        : '0 0 0 10px rgba(59, 130, 246, 0)',
    },
    '100%': {
      boxShadow: theme.palette.mode === 'dark' 
        ? '0 0 0 0 rgba(99, 102, 241, 0)'
        : '0 0 0 0 rgba(59, 130, 246, 0)',
    },
  },
}));

const MessageBubble = styled(Card)(({ theme, type }) => ({
  marginBottom: '16px',
  maxWidth: type === 'user' ? '70%' : '100%',
  marginLeft: type === 'user' ? 'auto' : '0',
  marginRight: type === 'user' ? '0' : 'auto',
  background: type === 'user' 
    ? theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
      : 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)'
    : type === 'response'
    ? theme.palette.mode === 'dark' 
      ? 'rgba(30, 41, 59, 0.7)'
      : 'rgba(255, 255, 255, 0.95)'
    : type === 'error'
    ? theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
      : 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)'
    : type === 'loading'
    ? theme.palette.mode === 'dark' 
      ? 'rgba(99, 102, 241, 0.15)'
      : 'rgba(59, 130, 246, 0.1)'
    : 'transparent',
  color: type === 'user' ? '#ffffff' : theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b',
  borderRadius: '20px',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 8px 32px rgba(0, 0, 0, 0.4)'
    : '0 8px 32px rgba(0, 0, 0, 0.08)',
  border: type === 'response' 
    ? theme.palette.mode === 'dark' 
      ? '1px solid rgba(99, 102, 241, 0.2)'
      : '1px solid rgba(148, 163, 184, 0.15)'
    : type === 'loading'
    ? theme.palette.mode === 'dark' 
      ? '2px solid rgba(99, 102, 241, 0.4)'
      : '2px solid rgba(59, 130, 246, 0.3)'
    : 'none',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  backdropFilter: 'blur(12px)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 16px 40px rgba(0, 0, 0, 0.5)'
      : '0 16px 40px rgba(0, 0, 0, 0.12)',
  }
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  width: '100%',
  maxHeight: '60vh',
  overflowY: 'auto',
  padding: '20px',
  marginBottom: '20px',
  scrollbarWidth: 'thin',
  scrollbarColor: theme.palette.mode === 'dark' 
    ? 'rgba(99, 102, 241, 0.6) rgba(30, 41, 59, 0.3)'
    : 'rgba(148, 163, 184, 0.6) rgba(248, 250, 252, 0.5)',
  scrollBehavior: 'smooth',
  
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.mode === 'dark' 
      ? 'rgba(30, 41, 59, 0.3)'
      : 'rgba(248, 250, 252, 0.5)',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark' 
      ? 'rgba(99, 102, 241, 0.6)'
      : 'rgba(148, 163, 184, 0.6)',
    borderRadius: '10px',
    '&:hover': {
      background: theme.palette.mode === 'dark' 
        ? 'rgba(99, 102, 241, 0.8)'
        : 'rgba(148, 163, 184, 0.8)',
    }
  },
  
  [theme.breakpoints.down("sm")]: {
    padding: '10px',
    maxHeight: '55vh',
  },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: '30px',
  textAlign: 'center',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
    : 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
  color: 'white',
  height: '56px',
  borderRadius: '16px',
  fontSize: '1rem',
  fontWeight: '600',
  marginLeft: '16px',
  paddingX: '32px',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 25px rgba(99, 102, 241, 0.4)'
    : '0 8px 25px rgba(59, 130, 246, 0.3)',
  border: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)'
      : 'linear-gradient(135deg, #2563eb 0%, #5b21b6 100%)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 12px 30px rgba(99, 102, 241, 0.6)'
      : '0 12px 30px rgba(59, 130, 246, 0.4)',
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    background: theme.palette.mode === 'dark'
      ? 'rgba(99, 102, 241, 0.3)'
      : 'rgba(59, 130, 246, 0.3)',
    color: 'rgba(255, 255, 255, 0.6)',
    boxShadow: 'none',
    transform: 'none',
  }
}));

// Auto-scroll button component
const AutoScrollButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: '140px',
  right: '30px',
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
    : 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
  color: 'white',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px rgba(99, 102, 241, 0.4)'
    : '0 8px 32px rgba(59, 130, 246, 0.3)',
  zIndex: 1000,
  animation: 'bounce 2s infinite',
  '&:hover': {
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)'
      : 'linear-gradient(135deg, #2563eb 0%, #5b21b6 100%)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 12px 40px rgba(99, 102, 241, 0.6)'
      : '0 12px 40px rgba(59, 130, 246, 0.4)',
  },
  '@keyframes bounce': {
    '0%, 20%, 50%, 80%, 100%': {
      transform: 'translateY(0)',
    },
    '40%': {
      transform: 'translateY(-10px)',
    },
    '60%': {
      transform: 'translateY(-5px)',
    },
  },
  
  [theme.breakpoints.down("sm")]: {
    bottom: '120px',
    right: '20px',
    width: '48px',
    height: '48px',
  },
}));

// Loading notification component
const LoadingNotification = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: '20px',
  right: '20px',
  background: theme.palette.mode === 'dark' 
    ? 'rgba(30, 41, 59, 0.9)'
    : 'rgba(255, 255, 255, 0.95)',
  color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b',
  padding: '16px 24px',
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 10px 40px rgba(0, 0, 0, 0.5)'
    : '0 10px 40px rgba(0, 0, 0, 0.1)',
  border: theme.palette.mode === 'dark' 
    ? '1px solid rgba(99, 102, 241, 0.3)'
    : '1px solid rgba(148, 163, 184, 0.2)',
  backdropFilter: 'blur(20px)',
  zIndex: 1001,
  
  [theme.breakpoints.down("sm")]: {
    top: '10px',
    right: '10px',
    left: '10px',
    padding: '12px 16px',
  },
}));
const SuggestedPromptChip = styled(Chip)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? 'rgba(99, 102, 241, 0.1)'
    : 'rgba(59, 130, 246, 0.08)',
  color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b',
  border: theme.palette.mode === 'dark' 
    ? '1px solid rgba(99, 102, 241, 0.3)'
    : '1px solid rgba(59, 130, 246, 0.2)',
  borderRadius: '12px',
  margin: '4px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
      : 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
    color: 'white',
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 25px rgba(99, 102, 241, 0.4)'
      : '0 8px 25px rgba(59, 130, 246, 0.3)',
  }
}));


const PodInsights = ({ collapsed }) => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [question, setQuestion] = useState('');
  const [showAutoScroll, setShowAutoScroll] = useState(false);
  const [showLoadingNotification, setShowLoadingNotification] = useState(false);
  const intl = useIntl();
  const theme = useTheme();

  // Refs for smart scrolling
  const messagesContainerRef = useRef(null);
  const lastQuestionRef = useRef(null);
  const lastResponseRef = useRef(null);
  const loadingRef = useRef(null);

  const { user } = useSelector(state => state.auth);
  const token = useAuthToken();
const [suggestedPrompts] = useState([
  "get count of problematic issues with no acceptance criteria",
  "get count and ids of jira issues with no acceptance criteria"
]);
  // Handle auto-scroll button visibility and loading notification
  useEffect(() => {
    if (isLoading) {
      setShowAutoScroll(true);
      setShowLoadingNotification(true);
      
      // Auto-scroll to loading section after a brief delay
      setTimeout(() => {
        scrollToAnalyzing();
      }, 300);
    } else {
      setShowAutoScroll(false);
      setShowLoadingNotification(false);
    }
  }, [isLoading]);

  // Smart auto-scroll logic
  useEffect(() => {
    const scrollToElement = () => {
      if (!messagesContainerRef.current) return;

      const container = messagesContainerRef.current;
      
      if (isLoading) {
        // While loading, scroll to the loading section
        if (loadingRef.current) {
          loadingRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest' 
          });
        }
      } else if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        
        if (lastMessage.type === 'user') {
          // New question: scroll to show the question
          if (lastQuestionRef.current) {
            lastQuestionRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest' 
            });
          }
        } else if (lastMessage.type === 'response') {
          // New response: scroll to show the response from the beginning
          if (lastResponseRef.current) {
            lastResponseRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start',
              inline: 'nearest' 
            });
          }
        }
      }
    };

    // Use timeout to ensure DOM is updated
    const timeoutId = setTimeout(scrollToElement, 200);
    
    return () => clearTimeout(timeoutId);
  }, [messages, isLoading]);

  // Function to scroll to analyzing section
  const scrollToAnalyzing = () => {
    if (loadingRef.current) {
      loadingRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center',
        inline: 'nearest' 
      });
    } else if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Function to get appropriate icon based on data type
  const getDataIcon = (data, question) => {
    const questionLower = question.toLowerCase();
    if (questionLower.includes('stakeholder') || questionLower.includes('user')) return <PersonIcon />;
    if (questionLower.includes('candidate') || questionLower.includes('talent')) return <GroupIcon />;
    if (questionLower.includes('pod') || questionLower.includes('activity')) return <AssignmentIcon />;
    if (questionLower.includes('blocker')) return <WarningIcon />;
    return <CheckIcon />;
  };

  // Enhanced formatting with better visual hierarchy
  const formatResponse = (responseData, question) => {
    if (typeof responseData === 'string' && responseData.includes('\n • ')) {
      return formatPodInsights(responseData);
    }
    
    if (Array.isArray(responseData)) {
      return formatTableData(responseData, question);
    }
    
    if (typeof responseData === 'object' && responseData !== null) {
      return formatObjectData(responseData);
    }
    
    return (
      <ModernCard>
        <CardContent>
          <Typography variant="body1" sx={{ fontSize: '16px', lineHeight: 1.6 }}>
            {String(responseData)}
          </Typography>
        </CardContent>
      </ModernCard>
    );
  };

  const formatPodInsights = (responseData) => {
    const insights = responseData.split('\n • ').slice(1);
    return insights.map((insightData, index) => (
      <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }} key={index}>
        <ModernCard>
          <CardContent>
            {insightData.includes("blocker:") ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ 
                    bgcolor: theme.palette.mode === 'dark' ? '#ef4444' : '#f87171', 
                    mr: 2, 
                    width: 40, 
                    height: 40 
                  }}>
                    <WarningIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 'bold', 
                    color: theme.palette.mode === 'dark' ? '#ef4444' : '#dc2626' 
                  }}>
                    Blocker Detected
                  </Typography>
                </Box>
                {insightData.split(/reported_date:|talent_name:/).map((part, i) => (
                  <Typography key={i} variant="body1" sx={{ mb: 1, fontSize: '16px' }}>
                    {part.trim()}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <Avatar sx={{ 
                  bgcolor: theme.palette.mode === 'dark' ? '#6366f1' : '#3b82f6', 
                  mr: 2, 
                  mt: 0.5, 
                  width: 40, 
                  height: 40 
                }}>
                  <CheckIcon />
                </Avatar>
                <Typography variant="body1" sx={{ fontSize: '16px', lineHeight: 1.6 }}>
                  {insightData}
                </Typography>
              </Box>
            )}
          </CardContent>
        </ModernCard>
      </Zoom>
    ));
  };

  const formatTableData = (data, question) => {
    if (data.length === 0) {
      return (
        <ModernCard>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" sx={{ 
              color: theme.palette.mode === 'dark' ? '#64748b' : '#64748b' 
            }}>
              No data found
            </Typography>
          </CardContent>
        </ModernCard>
      );
    }

    const headers = Object.keys(data[0]);
    const icon = getDataIcon(data, question);
    
    return (
      <Fade in={true}>
        <ModernCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ 
                bgcolor: theme.palette.mode === 'dark' ? '#6366f1' : '#3b82f6', 
                mr: 2, 
                width: 48, 
                height: 48 
              }}>
                {icon}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Query Results
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: theme.palette.mode === 'dark' ? '#64748b' : '#64748b' 
                }}>
                  Found {data.length} record{data.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>
            
            <TableContainer sx={{ 
              borderRadius: '12px', 
              overflow: 'hidden',
              border: theme.palette.mode === 'dark' 
                ? '1px solid rgba(99, 102, 241, 0.2)'
                : '1px solid rgba(148, 163, 184, 0.2)'
            }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ 
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(99, 102, 241, 0.1)' 
                      : 'rgba(59, 130, 246, 0.05)' 
                  }}>
                    {headers.map((header) => (
                      <TableCell 
                        key={header} 
                        sx={{ 
                          fontWeight: 'bold', 
                          textTransform: 'capitalize',
                          fontSize: '14px',
                          py: 2,
                          color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b'
                        }}
                      >
                        {header.replace(/_/g, ' ')}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((row, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:hover': { 
                          bgcolor: theme.palette.mode === 'dark' 
                            ? 'rgba(99, 102, 241, 0.05)' 
                            : 'rgba(59, 130, 246, 0.02)' 
                        },
                        '&:last-child td': { border: 0 },
                        borderBottom: theme.palette.mode === 'dark' 
                          ? '1px solid rgba(99, 102, 241, 0.1)'
                          : '1px solid rgba(148, 163, 184, 0.1)'
                      }}
                    >
                      {headers.map((header) => (
                        <TableCell key={header} sx={{ 
                          py: 2,
                          color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b'
                        }}>
                          {renderCellValue(row[header], header)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </ModernCard>
      </Fade>
    );
  };

  const formatObjectData = (data) => {
    return (
      <ModernCard>
        <CardContent>
          <Grid container spacing={2}>
            {Object.entries(data).map(([key, value]) => (
              <Grid item xs={12} sm={6} key={key}>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: theme.palette.mode === 'dark' 
                    ? 'rgba(99, 102, 241, 0.1)' 
                    : 'rgba(59, 130, 246, 0.05)', 
                  borderRadius: '12px',
                  border: theme.palette.mode === 'dark' 
                    ? '1px solid rgba(99, 102, 241, 0.2)'
                    : '1px solid rgba(148, 163, 184, 0.15)'
                }}>
                  <Typography variant="caption" sx={{ 
                    textTransform: 'uppercase', 
                    letterSpacing: 1,
                    color: theme.palette.mode === 'dark' ? '#64748b' : '#64748b'
                  }}>
                    {key.replace(/_/g, ' ')}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: '500', 
                    mt: 0.5,
                    color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b'
                  }}>
                    {String(value)}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </ModernCard>
    );
  };

  const renderCellValue = (value, header) => {
    if (value === null || value === undefined || value === '') {
      return <Typography variant="body2" sx={{ 
        color: theme.palette.mode === 'dark' ? '#64748b' : '#64748b' 
      }}>-</Typography>;
    }

    if (header.includes('date') || header.includes('At')) {
      try {
        const date = new Date(value);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DateIcon sx={{ 
              mr: 1, 
              fontSize: 16, 
              color: theme.palette.mode === 'dark' ? '#64748b' : '#64748b' 
            }} />
            <Typography variant="body2">{date.toLocaleDateString()}</Typography>
          </Box>
        );
      } catch (e) {
        return String(value);
      }
    }

    if (header.includes('status') || header.includes('approved')) {
      return (
        <Chip 
          label={String(value)} 
          size="small" 
          color={value === 'Approved' ? 'success' : 'default'}
          sx={{ 
            borderRadius: '16px', 
            fontWeight: '500',
            bgcolor: value === 'Approved' 
              ? theme.palette.mode === 'dark' ? '#22c55e' : '#16a34a'
              : theme.palette.mode === 'dark' ? '#64748b' : '#94a3b8',
            color: '#ffffff'
          }}
        />
      );
    }

    if (String(value).length > 50) {
      return (
        <Typography variant="body2" sx={{ maxWidth: '200px' }}>
          {String(value).substring(0, 47)}...
        </Typography>
      );
    }

    return <Typography variant="body2">{String(value)}</Typography>;
  };

  const handleSendClick = async () => {
    if (!question.trim()) {
      setErrorMessage('Please enter a question');
      setTimeout(() => setErrorMessage(null), 3000);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    // Add user question to messages first
    const userMessage = { type: 'user', text: question };
    setMessages(prev => [...prev, userMessage]);
    setQuestion('');

    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    const payload = {
      "manager_id": user?.data[0]?.emp_id,
      "question": question
    };

    try {
      const response = await axios.post(
        process.env.REACT_APP_CLIENT_PROMTSNQL,
        payload,
        { headers: headers }
      );

      let responseData = null;
      let formattedResponse = null;

      if (response?.data?.pod_insights) {
        responseData = response.data.pod_insights;
        formattedResponse = formatResponse(responseData, question);
      } else if (response?.data?.body && Array.isArray(response.data.body)) {
        responseData = response.data.body;
        formattedResponse = formatResponse(responseData, question);
      } else if (response?.data?.body) {
        responseData = response.data.body;
        formattedResponse = formatResponse(responseData, question);
      } else if (response?.data) {
        responseData = response.data;
        formattedResponse = formatResponse(responseData, question);
      } else {
        throw new Error('No data found in the response');
      }

      setMessages(prev => [...prev, { type: 'response', text: formattedResponse }]);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setErrorMessage('Error fetching data: ' + error.message);
      // Remove the user message if there was an error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendClick();
    }
  };

  return (
    <Container collapsed={collapsed}>
      <MainContent collapsed={collapsed}>
        <HeaderSection>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 'bold', 
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                : 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            {intl.formatMessage({ id: 'podQuery' })}
          </Typography>
          <Typography variant="h6" sx={{ 
            color: theme.palette.mode === 'dark' ? '#e7edf5ff' : '#64748b', 
            fontWeight: 400 
          }}>
           {intl.formatMessage({ id: 'askQuestionsAboutYourPod' })}
          </Typography>
        </HeaderSection>
        
        <MessagesContainer ref={messagesContainerRef}>
         {messages.length === 0 && !isLoading && (
  <Box sx={{ textAlign: 'center', py: 8 }}>
    <Typography variant="h6" sx={{ 
      mb: 2,
      color: theme.palette.mode === 'dark' ? '#cad5e4ff' : '#64748b'
    }}>
      👋{intl.formatMessage({ id: 'welcomeToPodInsights' })}
    </Typography>
    {/* <Typography variant="body1" sx={{ 
      maxWidth: 600, 
      mx: 'auto',
      color: theme.palette.mode === 'dark' ? '#bec8d5ff' : '#64748b'
    }}>
      {intl.formatMessage({ id: 'startByAskingQuestionsLike' })}
      <br />•  {intl.formatMessage({ id: 'showAllPodActivitiesThatHadBlockers' })}
      <br />• {intl.formatMessage({ id: 'listAllCandidatesAndTheirPods' })}
      <br />•  {intl.formatMessage({ id: 'getCountIsseswithNoCritria' })}
    </Typography> */}
  </Box>
)}
          
          {messages.map((message, index) => (
            <Fade in={true} key={index} timeout={500}>
              <MessageBubble 
                type={message.type}
                ref={message.type === 'user' && index === messages.length - 1 ? lastQuestionRef : 
                     message.type === 'response' && index === messages.length - 1 ? lastResponseRef : null}
              >
                <CardContent sx={{ p: message.type === 'user' ? 2 : 0 }}>
                  {message.type === 'response' ? message.text : (
                    <Typography sx={{ fontSize: '16px', lineHeight: 1.5 }}>
                      {message.text}
                    </Typography>
                  )}
                </CardContent>
              </MessageBubble>
            </Fade>
          ))}
          
          {isLoading && (
            <Fade in={true}>
              <MessageBubble type="loading" ref={loadingRef}>
                <CardContent>
                  <LoadingContainer>
                    <CircularProgress size={40} sx={{ 
                      color: theme.palette.mode === 'dark' ? '#6366f1' : '#3b82f6' 
                    }} />
                    <Typography variant="body1" sx={{ fontWeight: '500' }}>
                    {intl.formatMessage({ id: 'analyzingYourQuestion' })}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: theme.palette.mode === 'dark' ? '#64748b' : '#64748b' 
                    }}>
                      {intl.formatMessage({ id: 'processingDataAndGeneratingInsights' })}
                    </Typography>
                  </LoadingContainer>
                </CardContent>
              </MessageBubble>
            </Fade>
          )}
          
          {errorMessage && (
            <Fade in={true}>
              <Alert 
                severity="error" 
                sx={{ 
                  borderRadius: '12px', 
                  mb: 2,
                  fontSize: '16px',
                  bgcolor: theme.palette.mode === 'dark' 
                    ? 'rgba(239, 68, 68, 0.1)' 
                    : 'rgba(248, 113, 113, 0.1)',
                  color: theme.palette.mode === 'dark' ? '#ef4444' : '#dc2626',
                  border: theme.palette.mode === 'dark' 
                    ? '1px solid rgba(239, 68, 68, 0.3)'
                    : '1px solid rgba(248, 113, 113, 0.3)'
                }}
                onClose={() => setErrorMessage(null)}
              >
                {errorMessage}
              </Alert>
            </Fade>
          )}
        </MessagesContainer>
        
       <InputContainer>
  <Autocomplete
    freeSolo
    options={suggestedPrompts}
    value={question}
    onInputChange={(event, newValue) => {
      setQuestion(newValue || '');
    }}
    onChange={(event, newValue) => {
      if (newValue) {
        setQuestion(newValue);
      }
    }}
      fullWidth
  ListboxProps={{
    style: {
      maxHeight: '200px',
    }
  }}
    renderInput={(params) => (
      <StyledTextField
        {...params}
        fullWidth
        onKeyPress={handleKeyPress}
        placeholder={intl.formatMessage({ id: 'askAnythingAboutYourPods' })}
        variant="outlined"
        disabled={isLoading}
        multiline
        maxRows={3}
        sx={{
          '& .MuiInputBase-input': {
            fontSize: '16px',
            fontWeight: '400',
            color: theme.palette.mode === 'dark' ? '#e2e8f0' : '#1e293b',
            '&::placeholder': {
              color: theme.palette.mode === 'dark' ? '#cdd2d8ff' : '#64748b',
              opacity: 1,
              fontWeight: '400'
            }
          }
        }}
      />
    )}
    renderOption={(props, option) => (
      <Box 
        component="li" 
        {...props}
        sx={{
          fontSize: '14px',
          padding: '12px 16px',
            whiteSpace: 'normal', // Add this to prevent text cutting
        wordWrap: 'break-word', 
          borderBottom: theme.palette.mode === 'dark' 
            ? '1px solid rgba(99, 102, 241, 0.1)'
            : '1px solid rgba(148, 163, 184, 0.1)',
          '&:hover': {
            background: theme.palette.mode === 'dark' 
              ? 'rgba(99, 102, 241, 0.1)'
              : 'rgba(59, 130, 246, 0.05)',
          }
        }}
      >
        {option}
      </Box>
    )}
    PaperComponent={({ children, ...other }) => (
      <Paper 
        {...other}
        sx={{
          background: theme.palette.mode === 'dark' 
            ? 'rgba(30, 41, 59, 0.95)'
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '12px',
          border: theme.palette.mode === 'dark' 
            ? '1px solid rgba(99, 102, 241, 0.2)'
            : '1px solid rgba(148, 163, 184, 0.2)',
          boxShadow: theme.palette.mode === 'dark'
            ? '0 10px 40px rgba(0, 0, 0, 0.5)'
            : '0 10px 40px rgba(0, 0, 0, 0.1)',
          maxHeight: '200px',
          overflow: 'auto',
            width: '100%', // Add this
        marginTop: '4px'
        }}
      >
        {children}
      </Paper>
    )}
  />
  <StyledButton 
    onClick={handleSendClick} 
    disabled={isLoading || !question.trim()}
    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
  >
    {isLoading ? intl.formatMessage({ id: 'sending' }): intl.formatMessage({ id: 'send' })}
  </StyledButton>
</InputContainer>
      </MainContent>

      {/* Auto-scroll button */}
      {showAutoScroll && (
        <Fade in={showAutoScroll}>
          <AutoScrollButton
            onClick={scrollToAnalyzing}
            size="medium"
            aria-label="Scroll to analyzing results"
          >
            <ScrollDownIcon />
          </AutoScrollButton>
        </Fade>
      )}

      {/* Loading notification */}
      {showLoadingNotification && (
        <Fade in={showLoadingNotification}>
          <LoadingNotification>
            <CircularProgress size={20} sx={{ 
              color: theme.palette.mode === 'dark' ? '#6366f1' : '#3b82f6' 
            }} />
            <Typography variant="body2" sx={{ fontWeight: '500' }}>
            {intl.formatMessage({ id: 'analyzingYourQuestion' })}
            </Typography>
          </LoadingNotification>
        </Fade>
      )}
    </Container>
  );
};

export default PodInsights;